/**
 * CSV Utilities for Address Book Import/Export
 */

// CSV field headers
const CSV_HEADERS = ["name", "phone", "email", "company", "notes"];
const CSV_HEADER_ROW = CSV_HEADERS.join(",");

/**
 * Parse CSV string into array of contact objects
 * @param {string} csvString - Raw CSV content
 * @returns {{contacts: Array, errors: Array}}
 */
export const parseCSV = (csvString) => {
  const contacts = [];
  const errors = [];

  if (!csvString || typeof csvString !== "string") {
    errors.push({ row: 0, message: "Invalid or empty CSV content" });
    return { contacts, errors };
  }

  // Split by newlines and filter empty lines
  const lines = csvString
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    errors.push({ row: 0, message: "CSV file is empty" });
    return { contacts, errors };
  }

  // Parse header row
  const headerLine = lines[0].toLowerCase();
  const headers = parseCSVLine(headerLine);

  // Validate headers
  const requiredHeaders = ["name"];
  const hasRequiredHeaders = requiredHeaders.every((h) =>
    headers.some((header) => header.toLowerCase() === h)
  );

  if (!hasRequiredHeaders) {
    errors.push({ row: 1, message: "CSV must have a 'name' column" });
    return { contacts, errors };
  }

  // Create header index map
  const headerMap = {};
  headers.forEach((header, index) => {
    const normalizedHeader = header.toLowerCase().trim();
    if (CSV_HEADERS.includes(normalizedHeader)) {
      headerMap[normalizedHeader] = index;
    }
  });

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const values = parseCSVLine(line);
    const rowNumber = i + 1;

    try {
      const contact = {};

      // Map values to contact fields
      CSV_HEADERS.forEach((field) => {
        if (headerMap[field] !== undefined) {
          contact[field] = values[headerMap[field]]?.trim() || "";
        } else {
          contact[field] = "";
        }
      });

      // Validate required fields
      if (!contact.name || contact.name.length < 2) {
        errors.push({
          row: rowNumber,
          message: `Row ${rowNumber}: Name is required and must be at least 2 characters`,
          data: contact,
        });
        continue;
      }

      // Validate email format if provided
      if (contact.email && !isValidEmail(contact.email)) {
        errors.push({
          row: rowNumber,
          message: `Row ${rowNumber}: Invalid email format`,
          data: contact,
        });
        continue;
      }

      // Validate phone format if provided
      if (contact.phone && !isValidPhone(contact.phone)) {
        errors.push({
          row: rowNumber,
          message: `Row ${rowNumber}: Invalid phone format`,
          data: contact,
        });
        continue;
      }

      contacts.push(contact);
    } catch (error) {
      errors.push({
        row: rowNumber,
        message: `Row ${rowNumber}: Failed to parse - ${error.message}`,
      });
    }
  }

  return { contacts, errors };
};

/**
 * Parse a single CSV line handling quoted values
 * @param {string} line - CSV line
 * @returns {Array} - Array of values
 */
const parseCSVLine = (line) => {
  const values = [];
  let currentValue = "";
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && !insideQuotes) {
      insideQuotes = true;
    } else if (char === '"' && insideQuotes) {
      if (nextChar === '"') {
        // Escaped quote
        currentValue += '"';
        i++;
      } else {
        // End of quoted value
        insideQuotes = false;
      }
    } else if (char === "," && !insideQuotes) {
      values.push(currentValue.trim());
      currentValue = "";
    } else {
      currentValue += char;
    }
  }

  // Add last value
  values.push(currentValue.trim());

  return values;
};

/**
 * Validate contacts before import
 * @param {Array} contacts - Array of contact objects
 * @returns {{valid: Array, duplicates: Array, invalid: Array}}
 */
export const validateContacts = (contacts, existingPhones = []) => {
  const valid = [];
  const duplicates = [];
  const invalid = [];
  const seenPhones = new Set(existingPhones.map((p) => p?.replace(/\s/g, "")));

  contacts.forEach((contact, index) => {
    // Check required fields
    if (!contact.name || contact.name.length < 2) {
      invalid.push({ ...contact, _index: index, _reason: "Name required" });
      return;
    }

    // Check for duplicate phone in import set
    if (contact.phone) {
      const normalizedPhone = contact.phone.replace(/\s/g, "");
      if (seenPhones.has(normalizedPhone)) {
        duplicates.push({ ...contact, _index: index, _reason: "Duplicate phone" });
        return;
      }
      seenPhones.add(normalizedPhone);
    }

    valid.push(contact);
  });

  return { valid, duplicates, invalid };
};

/**
 * Generate CSV string from contacts array
 * @param {Array} contacts - Array of contact objects
 * @returns {string} - CSV content
 */
export const generateCSV = (contacts) => {
  if (!contacts || contacts.length === 0) {
    return CSV_HEADER_ROW + "\n";
  }

  const rows = [CSV_HEADER_ROW];

  contacts.forEach((contact) => {
    const values = CSV_HEADERS.map((header) => {
      const value = contact[header] || "";
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    });
    rows.push(values.join(","));
  });

  return rows.join("\n");
};

/**
 * Download contacts as CSV file
 * @param {Array} contacts - Array of contact objects
 * @param {string} filename - Output filename
 */
export const downloadCSV = (contacts, filename = "contacts.csv") => {
  const csvContent = generateCSV(contacts);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, filename);
  } else {
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Generate empty CSV template with API-compatible column names
 * @returns {string} - CSV template content
 */
export const generateTemplate = () => {
  // Columns: FullName, PhoneNumber, PhoneCountryCode, PhoneCountryIso, Email, Company, Notes
  // PhoneCountryCode and PhoneCountryIso are required when PhoneNumber is provided.
  const rows = [
    "FullName,PhoneNumber,PhoneCountryCode,PhoneCountryIso,Email,Company,Notes",
    // 10 rows with phone number (PhoneCountryCode + PhoneCountryIso required when PhoneNumber is provided)
    "John Smith,5551234501,1,us,john@example.com,Skyline,Primary field technician for onsite support.",
    "Michael Carter,5551234502,1,ca,michael@example.com,BrightWave,Responsible for quality assurance verification.",
    "Olivia Parker,5551234503,44,gb,oliva@example.com,GreenLeaf,Customer prefers communication via SMS.",
    "Emily Johnson,5551234504,91,in,emily@example.com,SilverOak,Frequently coordinates with remote support agents.",
    "James Wilson,5551234505,63,ph,james@example.com,BlueRiver,Backup contact for regional operations team.",
    "Daniel Martinez,5551234506,65,sg,danial@example.com,UrbanNest,Main contact for warehouse logistics updates.",
    "Ava Thompson,5551234507,61,au,ava@example.com,NovaPulse,Handles equipment installation and maintenance.",
    "Sophia Turner,5551234508,971,ae,sophia@example.com,GoldenBridge,Available for video inspection sessions when required.",
    "Matthew Collins,5551234509,49,de,matthew@example.com,CloudCore,Emergency contact for after-hours service requests.",
    "David Anderson,5551234510,31,nl,david@example.com,IronGate,Temporary contact for field installation phase.",
  ];
  return rows.join("\n");
};

/**
 * Download CSV template file
 */
export const downloadTemplate = () => {
  const templateContent = generateTemplate();
  const blob = new Blob([templateContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.href = url;
  link.setAttribute("download", "contacts_template.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Validate email format
 * @param {string} email
 * @returns {boolean}
 */
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Validate phone format
 * @param {string} phone
 * @returns {boolean}
 */
const isValidPhone = (phone) => {
  const normalized = phone.replace(/\s/g, "");
  return /^[+]?[\d\-()]{7,20}$/.test(normalized);
};

const csvUtils = {
  parseCSV,
  validateContacts,
  generateCSV,
  downloadCSV,
  generateTemplate,
  downloadTemplate,
};

export default csvUtils;
